import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import { exportToExcel, exportToPDF } from "@/lib/export";

const EXPORT_COLS = [
  { header: "Tanggal", accessor: (r) => r.tanggal_panen },
  { header: "Tambak", accessor: (r) => r.tambak_nama },
  { header: "Berat (kg)", accessor: (r) => r.berat_total_kg },
  { header: "Size", accessor: (r) => r.size },
  { header: "Harga/kg", accessor: (r) => r.harga_per_kg },
  { header: "Total Nilai", accessor: (r) => r.total_nilai },
  { header: "Pembeli", accessor: (r) => r.pembeli || "-" },
  { header: "Status", accessor: (r) => r.status },
];

export default function PanenPage() {
  const [items, setItems] = useState([]);
  const [siklusList, setSiklusList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ siklus_id: "", tambak_id: "", tanggal_panen: "", berat_total_kg: 0, size: 0, harga_per_kg: 0, pembeli: "", keterangan: "" });

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/panen?page=${page}&limit=15&search=${search}`),
        api.get("/siklus?limit=500")
      ]);
      setItems(p.data.data);
      setTotal(p.data.total);
      setSiklusList(s.data.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/panen", form);
      toast.success("Data panen berhasil dicatat");
      setDialogOpen(false);
      setForm({ siklus_id: "", tambak_id: "", tanggal_panen: "", berat_total_kg: 0, size: 0, harga_per_kg: 0, pembeli: "", keterangan: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);
  const totalBerat = items.reduce((a, b) => a + (b.berat_total_kg || 0), 0);
  const totalNilai = items.reduce((a, b) => a + (b.total_nilai || 0), 0);

  return (
    <div data-testid="panen-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Panen & Penjualan</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola data hasil panen dan penjualan udang</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons onExcel={() => exportToExcel(items, EXPORT_COLS, "data_panen")} onPDF={() => exportToPDF(items, EXPORT_COLS, "data_panen", "Data Panen P3UW")} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-panen-btn" className="bg-amber-500 hover:bg-amber-600 text-white"><Plus size={18} className="mr-2" /> Catat Panen</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Data Panen Baru</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Siklus</Label>
                    <Select value={form.siklus_id} onValueChange={v => { const s = siklusList.find(x => x._id === v); setForm({ ...form, siklus_id: v, tambak_id: s?.tambak_id || "" }); }}>
                      <SelectTrigger data-testid="select-panen-siklus"><SelectValue placeholder="Pilih siklus" /></SelectTrigger>
                      <SelectContent>{siklusList.map(s => <SelectItem key={s._id} value={s._id}>{s.tambak_nama} - {s.tanggal_tebar}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Panen</Label><Input data-testid="input-tanggal-panen" type="date" value={form.tanggal_panen} onChange={e => setForm({ ...form, tanggal_panen: e.target.value })} required /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Berat (kg)</Label><Input data-testid="input-berat-panen" type="number" step="0.1" value={form.berat_total_kg} onChange={e => setForm({ ...form, berat_total_kg: parseFloat(e.target.value) || 0 })} required /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Size</Label><Input type="number" value={form.size} onChange={e => setForm({ ...form, size: parseFloat(e.target.value) || 0 })} required /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Harga/kg</Label><Input type="number" value={form.harga_per_kg} onChange={e => setForm({ ...form, harga_per_kg: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Pembeli</Label><Input value={form.pembeli} onChange={e => setForm({ ...form, pembeli: e.target.value })} /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Keterangan</Label><Textarea value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} /></div>
                <Button type="submit" data-testid="submit-panen-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200"><CardContent className="p-5">
          <p className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Total Record</p>
          <p className="text-2xl font-semibold mt-1" style={{ fontFamily: "Work Sans" }}>{total}</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="p-5">
          <p className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Total Berat Halaman</p>
          <p className="text-2xl font-semibold mt-1" style={{ fontFamily: "Work Sans" }}>{totalBerat.toLocaleString("id-ID")} kg</p>
        </CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="p-5">
          <p className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Total Nilai Halaman</p>
          <p className="text-2xl font-semibold mt-1 text-[#008080]" style={{ fontFamily: "Work Sans" }}>{formatCurrency(totalNilai)}</p>
        </CardContent></Card>
      </div>

      <div className="relative w-full sm:w-72">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-panen" placeholder="Cari data panen..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Tanggal</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Tambak</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Berat (kg)</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Size</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Harga/kg</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Total Nilai</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Pembeli</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Belum ada data panen</TableCell></TableRow>
              ) : items.map(p => (
                <TableRow key={p._id} className="hover:bg-slate-50">
                  <TableCell className="text-sm">{p.tanggal_panen}</TableCell>
                  <TableCell className="text-sm font-medium">{p.tambak_nama}</TableCell>
                  <TableCell className="text-sm font-semibold">{p.berat_total_kg}</TableCell>
                  <TableCell className="text-sm">{p.size}</TableCell>
                  <TableCell className="text-sm">{formatCurrency(p.harga_per_kg)}</TableCell>
                  <TableCell className="text-sm font-semibold text-[#008080]">{formatCurrency(p.total_nilai)}</TableCell>
                  <TableCell className="text-sm">{p.pembeli || "-"}</TableCell>
                  <TableCell><Badge className={p.status === "selesai" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{p.status === "selesai" ? "Selesai" : p.status === "dijual" ? "Dijual" : "Belum Dijual"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
        </CardContent>
      </Card>
    </div>
  );
}
